// ============= Imports =============
import { AuthContextProps, tokenManager } from '@tw/tw-component-library';
import { ApiResponse } from '../types';

// ============= Types =============
interface GetDocumentParams {
  billNumber: string;
  getAccessToken: AuthContextProps['getAccessToken'];
}

interface UploadResponse {
  success: boolean;
  message: string;
  data?: any;
}

// ============= Constants =============
const IS_DEVELOPMENT = import.meta.env.MODE === 'development';
const PROD_BASE_URL = import.meta.env.VITE_REQUEST_URL;
const API_ENDPOINT = '/api/GetDocuments';
const SIDETRADE_SCOPE =
  'api://b922470e-04e2-4c04-84fc-e5ec591459ad/SidetradeFrontEndProd';
const HEADERS = {
  'x-functions-key': 'Test',
  'Content-Type': 'application/json',
};

// ============= Utility Functions =============
/**
 * Creates headers for API request
 * @param token Authentication token
 * @returns Headers object
 */
const createHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
  ...HEADERS,
});

/**
 * Constructs the API URL based on environment and parameters
 * @param clientMatter The client matter string
 * @returns The constructed URL
 */
const constructUploadUrl = (billNumber: string): string => {
  if (IS_DEVELOPMENT) {
    // In development, use relative URL for proxy
    return `${API_ENDPOINT}?&billNumber=${encodeURIComponent(billNumber)}`;
  } else {
    // In production, use full URL
    return `${PROD_BASE_URL}${API_ENDPOINT}?&billNumber=${encodeURIComponent(
      billNumber
    )}`;
  }
};

// ============= Main Upload Function =============
export const getDocuments = async ({
  billNumber,
  getAccessToken,
}: GetDocumentParams): Promise<ApiResponse<UploadResponse>> => {
  try {
    console.log(
      `🔄 Uploading document in ${
        IS_DEVELOPMENT ? 'development' : 'production'
      } mode...`
    );

    const url = constructUploadUrl(billNumber);
    console.log('🌐 Upload URL:', url);

    // Token acquisition with required scopes
    const token = await getAccessToken([SIDETRADE_SCOPE]);
    if (!token) {
      throw new Error('Failed to acquire Graph API token');
    }
    tokenManager.logTokenInfo(token, 'Graph API');

    const fetchOptions: RequestInit = {
      method: 'GET',
      headers: createHeaders(token),
      ...(IS_DEVELOPMENT && { credentials: 'include' }),
    };

    const response = await fetch(url, fetchOptions);
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Upload failed:', response.status, errorText);
      throw new Error(`Upload failed: ${response.status} ${errorText}`);
    }

    const data = await response.json();
    console.log('✅ Document uploaded successfully');

    return {
      success: true,
      data,
    };
  } catch (error) {
    console.error('🔴 Error uploading document:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Upload failed',
    };
  }
};
